import React, { useState, useEffect, useMemo } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from "recharts";

import "bootstrap/dist/css/bootstrap.min.css";
import { apiAxios } from "../../../../utils/apiAxios";
import "../../../../style/designerperformance.css"
import { DesignerData } from "../Interfaces/DesignerData";
import { SLAAlert } from "../Interfaces/SLAAlert";


const DesignerPerformance: React.FC = () => {

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const API_PATH = process.env.REACT_APP_API_PATH_URL;

  const [filter, setFilter] = useState("today")
  const [designers, setDesigners] = useState<DesignerData[]>([])
  const [slaAlerts, setSlaAlerts] = useState<SLAAlert[]>([])
  const [loading, setLoading] = useState(false)

  const getColor = (name: string) => {
    const colors = ["#1677ff", "#52c41a", "#fa8c16", "#eb2f96", "#722ed1"];
    let charCode = name.charCodeAt(0);
    return colors[charCode % colors.length];
  };
  /* KPI */

  const totalCases = useMemo(() => designers.reduce((s, d) => s + d.cases, 0), [designers])
  const totalCompleted = useMemo(() => designers.reduce((s, d) => s + d.completed, 0), [designers])

  const avgSLA = useMemo(() => {
    if (designers.length === 0) return "0"
    return (
      designers.reduce((s, d) => s + d.sla, 0) / designers.length
    ).toFixed(2)
  }, [designers])
  /* 🔥 SMART PERFORMANCE SCORE */
  const calculateScore = (d: DesignerData) => {

    if (!designers || designers.length === 0) return 0;

    const maxCompleted = Math.max(...designers.map(x => x.completed), 1);
    const maxAvgTime = Math.max(...designers.map(x => x.avgTime), 1);
    const maxActive = Math.max(...designers.map(x => x.activeCases), 1);

    const completedScore = d.completed / maxCompleted;
    const slaScore = d.sla / 100;
    const timeScore = 1 - (d.avgTime / maxAvgTime);
    const workloadScore = 1 - (d.activeCases / maxActive);

    return (
      completedScore * 0.4 +
      slaScore * 0.3 +
      timeScore * 0.2 +
      workloadScore * 0.1
    );
  };

  /* API */

  const fetchDesignerPerformance = async (selectedFilter: string) => {

    try {

      setLoading(true)

      const response = await apiAxios.get(
        `${API_BASE}/Central/designer-performance?filter=${selectedFilter}`
      )

      setDesigners(response.data.data ?? [])

    } catch (error) {

      console.error("Failed to fetch designer performance:", error)

    } finally {

      setLoading(false)

    }

  }

  const fetchSLAAlerts = async () => {

    try {

      const response = await apiAxios.get(
        `${API_BASE}/Central/designer-SLAAlerts`
      )

      const mapped: SLAAlert[] = (response.data.data ?? []).map((c: any) => ({

        caseID: c.caseID,
        orderID: c.orderID,
        remainingMinutes: c.remainingMinutes,
        alertLevel: c.alertLevel

      }))

      mapped.sort((a, b) => {

        const priority: any = {
          CRITICAL: 1,
          WARNING: 2,
          SAFE: 3
        }

        return priority[a.alertLevel] - priority[b.alertLevel]

      })

      setSlaAlerts(mapped)

    } catch (error) {

      console.error("Failed to fetch SLA alerts:", error)

    }

  }


  const rankingColumns: TableColumn<any>[] = [

    {
      name: "Rank",
      cell: (row, index) => (
        <div className="rank-number">{index + 1}</div>
      ),
      width: "80px"
    },

    {
      name: "Designer",
      selector: row => row.designer,
       width: "180px",
      sortable: true,
      cell: row => {

        const hasImage = row.image && row.image.trim() !== "";
        const firstLetter = row.designer?.charAt(0)?.toUpperCase();

        return (
        <div className="designer-info d-flex align-items-center">
  {hasImage ? (
    <img
      src={row.image}
    className="ranking-avatar"
      alt={row.designer}
    />
  ) : (
 
     <div className="avatar-fallback"
      style={{ background: getColor(row.designer) }}
    >
      {firstLetter}
    </div>
  )}

  <span className="designer-name">
    {row.designer}
  </span>
</div>
        );
      }
    },

    {
      name: "Completed",
         width: "100px",
      selector: row => row.completed,
      sortable: true,
      cell: row => (
        <span className="status-badge success">
          {row.completed}
        </span>
      )
    },

    {
      name: "SLA %",
      selector: row => row.sla,
      sortable: true,
      cell: row => (
        <span className="status-badge bg-white-ontime">
          {row.sla}%
        </span>
      )
    },

    {
      name: "Score",
      selector: row => row.score,
      sortable: true,
      cell: row => (
        <span className="status-badge warning">
          {row.score.toFixed(2)}
        </span>
      )
    },

    {
      name: "Queue",
      selector: row => row.activeCases,
      cell: row => (
        <span className={`status-badge ${row.activeCases > 0 ? "warning" : "success"}`}>
          {row.activeCases > 0 ? "In Queue" : "Free"}
        </span>
      )
    }

  ];

  const formatTime = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    return mins === 0
      ? `${hours} hr`
      : `${hours} hr ${mins} min`;
  };

  useEffect(() => {

    fetchDesignerPerformance(filter)
    fetchSLAAlerts()

    const interval = setInterval(() => {

      fetchDesignerPerformance(filter)
      fetchSLAAlerts()

    }, 30000)

    return () => clearInterval(interval)

  }, [filter])

  const totalQueue = useMemo(
    () => designers.reduce((sum, d) => sum + d.activeCases, 0),
    [designers]
  );
  /* TABLE */

  const columns: TableColumn<DesignerData>[] = [

    {
      name: "Designer",
      selector: row => row.designer,
      sortable: true,
      cell: row => (
        <div className="designer-name">
          {row.designer}
        </div>
      )
    },

    {
      name: "Cases",
      selector: row => row.cases,
      sortable: true
    },

    {
      name: "Completed",
      selector: row => row.completed,
      sortable: true
    },

    {
      name: "Avg Time",
      cell: row => {
        const minutes = row.avgTime || 0;

        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;

        return (
          <span>
            {hours > 0 ? `${hours}h ` : ""}
            {mins}m
          </span>
        );
      }
    },

    {
      name: "SLA %",
      cell: row => (

        <span className={`sla-pill ${row.sla < 90 ? "low" :
          row.sla < 95 ? "medium" :
            "high"
          }`}>

          {row.sla}%

        </span>

      )

    },

    {
      name: "Workload",
      cell: row => {

        const percent = Math.min(row.activeCases * 20, 100)

        return (

          <div className="workload-container">

            <div className="workload-bar">

              <div
                className="workload-progress"
                style={{ width: `${percent}%` }}
              ></div>

            </div>

            <span className="workload-count">
              {row.activeCases}
            </span>

          </div>

        )

      }
    }

  ]


  const ranking = useMemo(() => {

    if (!designers || designers.length === 0) return [];

    return designers
      .map(d => ({
        ...d,
        score: calculateScore(d)
      }))
      .sort((a, b) => (b as any).score - (a as any).score);

  }, [designers]);

  const bestDesigner = ranking.length > 0 ? ranking[0] : null
  const worstDesigner = ranking.length > 2? ranking[ranking.length - 1] : null
  return (

    <div className="dashboard">

      <h4 className="mb-4">
        Designer Performance Dashboard
      </h4>


      {/* FILTER */}
      <div className="filter-card">

        <div className="filter-header">
          📅 Filter Analytics
        </div>

        <div className="filter-buttons">

          <button
            className={`filter-btn ${filter === "today" ? "active" : ""}`}
            onClick={() => setFilter("today")}
          >
            Today
          </button>

          <button
            className={`filter-btn ${filter === "week" ? "active" : ""}`}
            onClick={() => setFilter("week")}
          >
            Week
          </button>

          <button
            className={`filter-btn ${filter === "month" ? "active" : ""}`}
            onClick={() => setFilter("month")}
          >
            Month
          </button>

        </div>

      </div>


      {/* KPI */}

      <div className="order-status-bar mb-4">

        <div className="status-item">
          <div className="status-count">{designers.length}</div>
          <div className="status-text bgnone">Designers</div>
        </div>

        <div className="status-item">
          <div className="status-count">{totalCases}</div>
          <div className="status-text">
            <span className="status-badgenew warning">Total Cases</span>
          </div>
        </div>

        <div className="status-item">
          <div className="status-count">{totalCompleted}</div>
          <div className="status-text">
            <span className="status-badgenew success">Completed</span>
          </div>
        </div>

        <div className="status-item">
          <div className="status-count">{avgSLA}%</div>
          <div className="status-text">
            <span className="status-badgenew bg-white-ontime"> Avg SLA</span>
          </div>
        </div>

        <div className="status-item">
          <div className="status-count">{totalQueue}</div>
          <div className="status-text">
            <span className="status-badgenew danger"> Queue</span>
          </div>
        </div>

      </div>
      {/* 🏆 BEST DESIGNER */}

      <div className="row mt-4">

        {/* 🏆 BEST DESIGNER */}
        <div className="col-md-6">

          {bestDesigner && (

            <div className="designer-card best-card h-100">

              <div className="card-header">
                Best Designer
              </div>

              <div className="designer-body">
                <div className="avatar-wrapper">

                  {bestDesigner?.profileImage ? (
                    <img
                      src={`${API_PATH}/${bestDesigner.profileImage}`}
                      className="designer-avatar"
                      alt={bestDesigner.designer}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        const img = e.currentTarget;
                        img.style.display = "none";

                        const fallback = img.nextElementSibling as HTMLElement | null;
                        if (fallback) fallback.style.display = "flex";
                      }}
                    />
                  ) : null}

                  <div
                    className="avatar-fallback"
                    style={{
                      display: bestDesigner?.profileImage ? "none" : "flex",
                      background: getColor(bestDesigner?.designer || "")
                    }}
                  >
                    {bestDesigner?.designer?.charAt(0)?.toUpperCase()}
                  </div>

                </div>
                <div className="designer-info">

                  <h5>{bestDesigner.designer}</h5>

                  <div className="designer-stats">

                    <span className="status-badge success">
                      ✅ {bestDesigner.completed} Completed
                    </span>

                    <span className="status-badge bg-white-ontime">
                      ⚡ {bestDesigner.sla}% SLA
                    </span>

                    <span className="status-badge warning">
                      ⭐ {(bestDesigner as any).score.toFixed(2)} Score
                    </span>

                  </div>

                  <div className="status-badge success mt-2">
                    Top Performer
                  </div>

                </div>

              </div>

            </div>

          )}

        </div>

        {/* ⚠ WORST DESIGNER */}
        <div className="col-md-6">

          {worstDesigner && (

            <div className="designer-card worst-card h-100">

              <div className="card-header">
                Needs Improvement
              </div>

              <div className="designer-body">

                <div className="avatar-wrapper">

                  {worstDesigner?.profileImage ? (
                    <img
                      src={`${API_PATH}/${worstDesigner.profileImage}`}
                      className="designer-avatar"
                      alt={worstDesigner.designer}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        const img = e.currentTarget;
                        img.style.display = "none";

                        const fallback = img.nextElementSibling as HTMLElement | null;
                        if (fallback) {
                          fallback.style.display = "flex";
                        }
                      }}
                    />
                  ) : null}

                  <div
                    className="avatar-fallback "
                    style={{
                      display: worstDesigner?.profileImage ? "none" : "flex",
                      background: getColor(worstDesigner?.designer || "")
                    }}
                  >
                    {worstDesigner?.designer?.charAt(0)?.toUpperCase()}
                  </div>

                </div>

                <div className="designer-info">

                  <h5>{worstDesigner.designer}</h5>

                  <div className="designer-stats">

                    <span className="status-badge danger">
                      ❌ {worstDesigner.completed} Completed
                    </span>

                    <span className="status-badge bg-white-ontime">
                      ⚡ {worstDesigner.sla}% SLA
                    </span>

                    <span className="status-badge warning">
                      ⭐ {(worstDesigner as any).score.toFixed(2)} Score
                    </span>

                  </div>

                  <div className="status-badge danger mt-2">
                    Needs Attention
                  </div>

                </div>

              </div>

            </div>

          )}

        </div>

      </div>
      {/* CHART */}

      <div className="premium-card mt-4">

        <h4 className="mb-4">Designer Productivity</h4>

        <ResponsiveContainer width="100%" height={320}>

          <BarChart data={designers} barSize={40}>

            <CartesianGrid strokeDasharray="3 3" />

            <XAxis dataKey="designer" />

            <YAxis />

            <Tooltip />

            <Bar dataKey="completed" fill="#4facfe" radius={[10, 10, 0, 0]} />

          </BarChart>

        </ResponsiveContainer>

      </div>


      {/* LEADERBOARD */}
      <div className="premium-card mt-4">

        <h5 className="mb-3">Designer Ranking</h5>

        <DataTable
          columns={rankingColumns}
          data={ranking}
          pagination
          highlightOnHover
          striped
          className=""
        />

      </div>


      {/* SLA */}

      <div className="premium-card mt-4">

        <h5>SLA Alerts</h5>

        <div className="sla-grid">

          {slaAlerts.length === 0 ? (

            <div>No SLA alerts</div>

          ) : (

            slaAlerts.map((a, i) => (

              <div
                key={i}
                className={`sla-card ${a.alertLevel === "CRITICAL"
                  ? "critical"
                  : a.alertLevel === "WARNING"
                    ? "warning"
                    : "safe"
                  }`}
              >

                Case {a.orderID}

                <br />

                {formatTime(a.remainingMinutes)}

              </div>

            ))

          )}

        </div>

      </div>


      <div className="designer-load mt-4">

        <div className="load-header">
          Designer Load Distribution
        </div>

        <div className="load-list">

          {designers.length === 0 ? (

            <div className="no-data">No Designers</div>

          ) : (

            designers.map((d, i) => (

              <div className="load-row" key={i}>

                {/* Name */}
                <div className="load-name">
                  {d.designer}
                </div>

                {/* Jobs */}
                <div className="load-stats">

                  <span className="status-badge bg-white-ontime">
                    {d.activeCases} Jobs
                  </span>

                  <span
                    className={`status-badge ${d.activeCases >= 5
                      ? "danger"
                      : d.activeCases >= 3
                        ? "warning"
                        : "success"
                      }`}
                  >
                    {d.activeCases >= 5
                      ? "Overload"
                      : d.activeCases >= 3
                        ? "Balanced"
                        : "Free"}
                  </span>

                </div>

              </div>

            ))

          )}

        </div>

      </div>




      <div className="designer-queue mt-4">

        <div className="queue-header">
          Designers With Queue
        </div>

        <div className="queue-list">

          {designers.filter(d => d.activeCases > 0).length === 0 ? (

            <div className="no-data">No Queue</div>

          ) : (

            designers
              .filter(d => d.activeCases > 0)
              .map((d, i) => (

                <div key={i} className="queue-row">

                  {/* Name */}
                  <div className="queue-name">
                    {d.designer}
                  </div>

                  {/* Stats */}
                  <div className="queue-stats">

                    <span className="status-badge bg-white-ontime">
                      {d.activeCases} Orders
                    </span>

                    <span className="status-badge warning">
                      In Queue
                    </span>

                  </div>

                </div>

              ))

          )}

        </div>

      </div>
      {/* TABLE */}

      <div className="premium-card mt-4">

        <DataTable
          title="Designer Productivity Table"
          columns={columns}
          data={designers}
          progressPending={loading}
          pagination
          highlightOnHover
          striped
          className=""
        />

      </div>

    </div>

  )

}

export default DesignerPerformance